import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { AlertController, ModalController } from "ionic-angular";
import moment from "moment";
import { Subscription } from "rxjs/Subscription";
import CollapsibleEvent from "../../../../models/events/collapsible-modal.event";
import { BillingBenefitCode } from "../../../../models/payers/payer";
import { PayersService } from "../../../../providers/payers-service/payers.service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { NgbCustomParserFormatter } from "../../../common/date-picker/ngb-custom-parser-formatter";

@Component({
  selector: "billing-codes-hospice-loc",
  templateUrl: "hospice-loc.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BillingCodesHospiceLOC),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BillingCodesHospiceLOC),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BillingCodesHospiceLOC implements OnInit, OnDestroy {
  @Input() currentPayer: any;
  @Input() currentPayerType: any;
  @Output() onCanceled = new EventEmitter<any>();
  @Output() setCheckMark = new EventEmitter<any>();

  billingCodes: FormGroup;
  contractPeriodsOptions: any[] = [];
  selectedContractPeriod: any;
  useMedicareBillingCodes: boolean = false;
  showLevelOfCareDiv = false;
  showVisitsDiv = true;
  billingCodesLOCData: BillingBenefitCode[] = [];
  saveEnabled: true;
  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";
  dateRegex = new RegExp("\\d{4}-\\d{2}-\\d{2}");
  periodsResponse: any = [];
  minDateFormat: any = moment("2023-09-30", this.dateFormat);
  periodResponse: any = {};
  benefitSubtype: string = "LOC";
  benefitType: string = "HOSPICE";
  editMode: boolean = false;
  showHCPCSCodes: boolean = false;
  codesEditDisabled: boolean = false;
  validateModFields: boolean = true;
  hospiceLOCBillingCodesSetup: BillingBenefitCode[] = [];

  private payerType: string;
  californiaMediCalSupportToggle: boolean = false;
  private mediCalSupportSub: Subscription;

  // CO-3671 (epic CO-2315 / PRD REQ-007, DHCS APL 25-008 §V.A): a California
  // Medi-Cal payer (Medicaid + state CA, behind the california_medi_cal_support
  // flag) drives the Billing Codes tab off Medi-Cal revenue-code defaults, not
  // the Medicare "use default codes" path.
  get isMediCal(): boolean {
    return (
      this.californiaMediCalSupportToggle &&
      !!this.currentPayer &&
      String(this.currentPayer.payerType).toUpperCase() === "MEDICAID" &&
      String(this.currentPayer.state).toUpperCase() === "CA"
    );
  }

  // Medi-Cal routine/inpatient revenue-code defaults keyed by LOC service.
  // Seeded editable on a new contract period; never 0651 (APL 25-008 §V.A).
  private static readonly MEDI_CAL_REVENUE_CODES: {
    [service: string]: string;
  } = {
    ROUTINEHOMECAREHIGH: "0650",
    ROUTINEHOMECARELOW: "0659",
    CONTINUOUSCARE: "0652",
    GENERALINPATIENT: "0656",
    RESPITECARE: "0655",
  };

  private mediCalRevenueCode(service: string): string {
    return this.isMediCal
      ? BillingCodesHospiceLOC.MEDI_CAL_REVENUE_CODES[service] || ""
      : "";
  }

  constructor(
    private utilityService: UtilityService,
    private payersService: PayersService,
    private alertController: AlertController,
    private modalCtrl: ModalController,
    private fb: FormBuilder,
    private stateManagementService: StateManagementService
  ) {}

  setupBaseData() {
    const rows = [
      {
        Description: "Routine Home Care (1-60)",
        Service: "ROUTINEHOMECAREHIGH",
      },
      { Description: "Routine Home Care (61+)", Service: "ROUTINEHOMECARELOW" },
      { Description: "Continuous Home Care", Service: "CONTINUOUSCARE" },
      { Description: "General Inpatient Care", Service: "GENERALINPATIENT" },
      { Description: "Respite Care", Service: "RESPITECARE" },
    ];
    // For a Medi-Cal payer the revenue codes seed pre-filled (0650/0659/0652/
    // 0656/0655) and stay editable; all other payers keep the blank default.
    this.hospiceLOCBillingCodesSetup = rows.map((row) => ({
      RevenueCode: this.mediCalRevenueCode(row.Service),
      HcpcsCode: "",
      Description: row.Description,
      Service: row.Service,
      M1: "",
      M2: "",
      M3: "",
      M4: "",
    }));
  }

  ngOnDestroy(): void {
    if (this.mediCalSupportSub) {
      this.mediCalSupportSub.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.mediCalSupportSub =
      this.stateManagementService.californiaMediCalSupportToggle.subscribe(
        (enabled) => {
          this.californiaMediCalSupportToggle = !!enabled;
        }
      );
    this.showLevelOfCareDiv = true;
    this.useMedicareBillingCodes = false;
    this.billingCodesLOCData = [...this.hospiceLOCBillingCodesSetup];
    this.loadBenefitPeriods(this.benefitType, this.benefitSubtype, true);
    if (this.currentPayer && this.currentPayer.payerId) {
      this.payerType = this.currentPayer.payerType;
    }
    this.initForm();
    this.setupBaseData();
  }

  onPeriodChanged() {
    this.minDateFormat = moment("2023-09-30", this.dateFormat);
    this.loadBenefitPeriods(this.benefitType, this.benefitSubtype, true);
    this.selectedContractPeriod = null;
    this.billingCodesLOCData = [];
  }

  initForm() {
    this.billingCodes = this.fb.group({
      city: [],
      period: "10/01/2023",
    });
  }

  loadDefaults() {
    this.selectedContractPeriod = null;
    this.billingCodesLOCData = [];
    this.billingCodesLOCData = [...this.hospiceLOCBillingCodesSetup];
    this.useMedicareBillingCodes = false;
  }

  async onContractPeriodChange(event: any) {
    this.billingCodesLOCData = [];
    if (!event) {
      this.loadDefaults();
      this.showHCPCSCodes = true;
      return;
    }
    if (event.id === 0) {
      this.showHCPCSCodes = true;
      let contractPeriodModal = this.modalCtrl.create(
        "ContractPeriodModalComponent",
        { minDateFormat: this.minDateFormat },
        { cssClass: "contract-period-modal" }
      );
      contractPeriodModal.onDidDismiss((data) => {
        if (data) {
          let newContractPeriod = moment(new Date(data)).format(
            this.dateUIFormat
          );

          const newOption = {
            id: this.contractPeriodsOptions.length + 1,
            name: moment(new Date(data)).format(this.dateUIFormat),
            startDate: moment(data).format(this.dateFormat),
            endDate: null,
          };
          // Medi-Cal never uses the Medicare "default codes" path — leave the
          // flag false so UseDefaultCodes persists false and the seeded Medi-Cal
          // revenue codes stand.
          if (!this.isMediCal) {
            this.useMedicareBillingCodes = true;
            this.medicareCheckBoxChanged();
          }
          this.contractPeriodsOptions = [
            ...this.contractPeriodsOptions,
            newOption,
          ];
          this.sortBenefitCodesPeriods();
          this.selectedContractPeriod = newOption;
          this.contractPeriodsOptions.shift();
          this.billingCodesLOCData = [];
          this.setupBaseData();
          this.billingCodesLOCData = [...this.hospiceLOCBillingCodesSetup];
          this.editMode = false;
        } else {
          this.loadDefaults();
        }
      });

      contractPeriodModal.present();
    } else {
      this.selectedContractPeriod = event;
      this.selectedContractPeriod.startDate = moment(event.startDate).format(
        this.dateUIFormat
      );
      this.selectedContractPeriod.endDate = event.endDate
        ? moment(event.endDate).format(this.dateUIFormat)
        : "";
      this.utilityService.increaseSpinnerCount("Loading");
      this.showHCPCSCodes = true;
      this.loadBenefitPeriod(event);
      if (this.currentPayerType != "MEDICARE") {
        let filteredContractPeriods = this.contractPeriodsOptions.filter(
          (option) => option.startDate != this.selectedContractPeriod.startDate
        );
        // Second force-set path: a single-period non-Medicare payer. Skip it for
        // Medi-Cal so the flag is never silently persisted as true.
        if (filteredContractPeriods.length == 0 && !this.isMediCal) {
          this.useMedicareBillingCodes = true;
          this.medicareCheckBoxChanged();
        }
      }
    }
  }

  public onCollapseClicked($e: CollapsibleEvent): void {
    if ($e.controlId === "level-of-care") {
      this.showLevelOfCareDiv = $e.isOpen;
      this.showVisitsDiv = !$e.isOpen;
    } else if ($e.controlId === "visits") {
      this.showVisitsDiv = $e.isOpen;
      this.showLevelOfCareDiv = !$e.isOpen;
    }
  }

  public saveCodes(): void {
    let request = this.createRequest();
    this.utilityService.increaseSpinnerCount("Loading");
    this.editMode = true;
    this.payersService.postPayerBenefitCodes(request).subscribe(() => {
      setTimeout(() => {
        this.utilityService.decreaseSpinnerCount();
        let newOptionIndex = this.contractPeriodsOptions.findIndex(
          (option) => option.id === 0
        );
        this.loadBenefitPeriods(
          this.benefitType,
          this.benefitSubtype,
          newOptionIndex === -1
        );

        if (newOptionIndex === -1) this.addNewPeriodOption();
        this.editMode = false;
        this.showAlert({
          title: "Success",
          message: "Billing Codes saved.",
        });
        this.setCheckMark.emit();
      }, 2000);
    }, this.onSaveError);
  }

  showSavePayerModal() {
    if (
      this.currentPayer != null &&
      this.currentPayer.payerId &&
      this.selectedContractPeriod &&
      this.selectedContractPeriod.id > 0
    ) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Payer Information Change",
          commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
          popupMessageKey: "savePayerWarningMessage",
          section: "payer",
        },
        {
          enableBackdropDismiss: false,
          cssClass: "save-payer-confirmation-popup",
        }
      );
      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.saveCodes();
        }
      });
      modalPage.present();
    } else {
      this.saveCodes();
    }
  }

  addNewPeriodOption(): void {
    this.contractPeriodsOptions = [
      { id: 0, name: "Add New" },
      ...this.contractPeriodsOptions,
    ];
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: "Error while saving the rates. " + error.message,
        });
      }
    }
  };

  public createRequest(): any {
    let endDate = this.selectedContractPeriod
      ? moment(this.selectedContractPeriod.endDate).format(this.dateFormat)
      : null;
    if (!this.dateRegex.test(endDate)) endDate = null;
    let body = {
      payerId: this.currentPayer.payerId,
      dataType: "PayerServiceCodes",
      payerType: this.payerType,
      Start: moment(this.selectedContractPeriod.startDate).format(
        this.dateFormat
      ),
      End: endDate,
      BenefitType: this.benefitType,
      benefitSubType: this.benefitSubtype,
      UseDefaultCodes: this.useMedicareBillingCodes,
      Codes: {},
    };
    this.billingCodesLOCData.map((bc) => {
      body.Codes[bc.Service] = bc;
    });
    return body;
  }

  loadBenefitPeriods(payerType, subType, addNewOption) {
    let payerId = this.currentPayer.payerId;
    let payerTypeValue = payerType;
    let payerSubtype = subType;

    if (this.currentPayerType == "MEDICARE") {
      payerId = "MEDICARE";
      this.showVisitsDiv = false;
    }
    this.payersService
      .getPayerBenefitCodesPeriods(payerId, payerTypeValue, payerSubtype)
      .subscribe((response: any) => {
        this.periodsResponse = response;
        this.contractPeriodsOptions = [];
        this.mapBenefitPeriods(response, addNewOption);
        this.filterPeriodOptions();
        this.sortBenefitCodesPeriods();
      });
  }

  mapBenefitPeriods(response: any, addNew: boolean) {
    if (addNew) this.addNewPeriodOption();
    response.map((period) => {
      let name = moment(period.start, this.dateFormat).format(
        this.dateUIFormat
      );
      let sDate = moment(period.start).format(this.dateFormat);
      let minDate = moment(this.minDateFormat).format(this.dateFormat);
      if (moment(sDate).isAfter(moment(minDate))) {
        this.minDateFormat = period.start;
      }
      let endDate = null;
      if (period.end && this.dateRegex.test(period.end)) endDate = period.end;

      if (endDate) {
        name += "-" + moment(period.end).format(this.dateUIFormat);
      }

      this.contractPeriodsOptions.push({
        id:
          this.contractPeriodsOptions.length == 0
            ? 1
            : this.contractPeriodsOptions.length + 1,
        name: name,
        startDate: period.start,
        endDate: endDate,
      });
    });
  }

  public validateFields() {
    return (
      this.currentPayer != null &&
      this.currentPayer.payerId &&
      this.selectedContractPeriod &&
      this.selectedContractPeriod.id > 0 &&
      this.validateCodeFields(this.billingCodesLOCData) &&
      this.validateModFields
    );
  }

  public validateMod(data) {
    this.validateModFields = data;
  }

  public cancel(): void {
    this.contractPeriodsOptions = [];
    this.onCanceled.emit(true);
  }

  sortBenefitCodesPeriods() {
    this.contractPeriodsOptions = this.contractPeriodsOptions.sort((a, b) => {
      if (!a.startDate) return 0;
      if (!b.startDate) return 0;
      if (moment(a.startDate) > moment(b.startDate)) return -1;
      return 0;
    });
  }

  public validateCodeFields(configuration) {
    let hcpcsCodeValue =
      configuration.filter(
        (x) => x.HcpcsCode.length > 0 && x.HcpcsCode.length < 5
      ).length > 0
        ? false
        : true;
    let revenueCodeValue =
      configuration.filter(
        (x) =>
          x.RevenueCode.length !== null &&
          x.RevenueCode.length !== "" &&
          x.RevenueCode.length == 4
      ).length == 5
        ? true
        : false;

    if (hcpcsCodeValue && revenueCodeValue) return true;
    else return false;
  }

  public loadBenefitPeriod(benefitPeriod) {
    let payerId = this.currentPayer.payerId;
    let benefitType = this.benefitType;
    let benefitSubtype = this.benefitSubtype;
    let startDate = moment(benefitPeriod.startDate).format(this.dateFormat);
    let editMode = false;

    if (this.currentPayerType == "MEDICARE") {
      this.showHCPCSCodes = false;
      payerId = "MEDICARE";
      editMode = true;
    }
    this.payersService
      .getPayerBenefitCodesByPeriod(
        payerId,
        benefitType,
        benefitSubtype,
        startDate
      )
      .subscribe(
        (result: any) => {
          this.utilityService.decreaseSpinnerCount();
          this.periodResponse = result[0];
          if (this.periodResponse) {
            this.editMode = editMode;
            this.billingCodesLOCData = [];
            this.mapPeriodResponse();
            this.useMedicareBillingCodes = this.periodResponse.useDefaultCodes;
          } else {
            this.billingCodesLOCData = [...this.hospiceLOCBillingCodesSetup];
          }
        },
        () => {
          this.billingCodesLOCData = [];
          this.billingCodesLOCData = [...this.hospiceLOCBillingCodesSetup];
        }
      );
  }

  mapPeriodResponse() {
    for (var key in this.periodResponse.codes) {
      if (!this.periodResponse.codes.hasOwnProperty(key)) continue;
      var obj = this.periodResponse.codes[key];
      let newPeriod: BillingBenefitCode = {
        RevenueCode: obj.revenueCode,
        HcpcsCode: obj.hcpcsCode,
        Description: obj.description,
        Service: obj.service,
        M1: obj.m1,
        M2: obj.m2,
        M3: obj.m3,
        M4: obj.m4,
      };
      this.billingCodesLOCData.push(newPeriod);
    }

    const order = [
      "Routine Home Care (1-60)",
      "Routine Home Care (61+)",
      "Continuous Home Care",
      "General Inpatient Care",
      "Respite Care",
    ];

    this.billingCodesLOCData = this.billingCodesLOCData.sort((a, b) => {
      const indexA = order.indexOf(a.Description);
      const indexB = order.indexOf(b.Description);
      return indexA - indexB;
    });
  }

  filterPeriodOptions() {
    if (this.currentPayerType == "MEDICARE") {
      this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
        (option) => option.name != "Add New"
      );
    }
  }

  getEffectiveDate() {
    return this.selectedContractPeriod && this.selectedContractPeriod.startDate
      ? this.formatDate(this.selectedContractPeriod.startDate)
      : "";
  }

  getExpiredDate() {
    return this.selectedContractPeriod && this.selectedContractPeriod.endDate
      ? this.formatDate(this.selectedContractPeriod.endDate)
      : "";
  }

  formatDate(dateString: string): string {
    return moment(dateString).format(this.dateUIFormat);
  }

  async medicareCheckBoxChanged() {
    if (this.useMedicareBillingCodes) {
      await this.getMedicareDefaultValues();
    } else {
      if (this.selectedContractPeriod) {
        let payerId = this.currentPayer.payerId;
        let benefitType = this.benefitType;
        let benefitSubtype = this.benefitSubtype;
        let startDate = moment(this.selectedContractPeriod.startDate).format(
          this.dateFormat
        );
        this.utilityService.increaseSpinnerCount("Loading");
        this.payersService
          .getPayerBenefitCodesByPeriod(
            payerId,
            benefitType,
            benefitSubtype,
            startDate
          )
          .subscribe(
            async (result: any) => {
              this.utilityService.decreaseSpinnerCount();
              this.periodResponse = result[0];
              if (this.periodResponse) {
                this.mapPeriodResponseMedicareCheckbox();
              } else {
                await this.getMedicareDefaultValues();
              }
              this.codesEditDisabled = false;
              this.editMode = false;
            },
            () => {
              this.billingCodesLOCData = [];
              this.billingCodesLOCData = [...this.hospiceLOCBillingCodesSetup];
            }
          );
      }
    }
  }

  async getMedicareContractPeriods() {
    return new Promise((resolve, reject) => {
      this.payersService
        .getPayerBenefitCodesPeriods(
          "MEDICARE",
          this.benefitType,
          this.benefitSubtype
        )
        .subscribe(
          (response: any) => {
            if (response) {
              resolve(response);
            } else {
              resolve();
            }
          },
          (err) => {
            reject();
          }
        );
    });
  }

  async getActiveMedicareContractPeriod(payerId, startDate) {
    return new Promise((resolve, reject) => {
      this.payersService
        .getPayerBenefitCodesByPeriod(
          payerId,
          this.benefitType,
          this.benefitSubtype,
          startDate
        )
        .subscribe(
          (result: any) => {
            if (result) {
              resolve(result);
            } else {
              resolve();
            }
          },
          (err) => {
            reject();
          }
        );
    });
  }

  async getMedicareDefaultValues() {
    this.utilityService.increaseSpinnerCount("Loading Medicare Billing Codes");
    let medicareContractPeriods = (await this.getMedicareContractPeriods()) as {
      start: string;
      end: string;
    }[];

    function isSelectedDateWithinStartEnd(
      startDate: Date,
      selectedPeriodDate: Date,
      endDate: Date | null
    ) {
      if (endDate == null) {
        return startDate.getTime() <= selectedPeriodDate.getTime();
      }
      return (
        startDate.getTime() <= selectedPeriodDate.getTime() &&
        endDate.getTime() >= selectedPeriodDate.getTime()
      );
    }

    if (medicareContractPeriods && medicareContractPeriods.length > 0) {
      let activeMedicarePeriod = medicareContractPeriods
        .filter((period) => {
          let endDate = period["end"] == null ? null : new Date(period["end"]);
          let startDate = new Date(period["start"]);
          let selectedPeriodDate = new Date(
            this.selectedContractPeriod.startDate
          );
          if (
            isSelectedDateWithinStartEnd(startDate, selectedPeriodDate, endDate)
          ) {
            return period;
          }
          return null;
        })
        .pop();
      if (activeMedicarePeriod) {
        let activeMedicarePeriodBillingCodes =
          await this.getActiveMedicareContractPeriod(
            "MEDICARE",
            activeMedicarePeriod.start
          );
        if (activeMedicarePeriodBillingCodes) {
          this.periodResponse = activeMedicarePeriodBillingCodes[0];
          this.mapPeriodResponseMedicareCheckbox();
          this.codesEditDisabled = false;
          this.editMode = false;
        } else {
          this.showAlert({
            title: "Config Not Found",
            message: `Could not find Medicare Billing Codes configuration for: ${moment(
              this.selectedContractPeriod.startDate
            ).format(this.dateUIFormat)}.`,
          });
          this.billingCodesLOCData = [...this.hospiceLOCBillingCodesSetup];
        }
      } else {
        this.showAlert({
          title: "Config Not Found",
          message: `Could not find Medicare Billing Codes configuration for: ${moment(
            this.selectedContractPeriod.startDate
          ).format(this.dateUIFormat)}.`,
        });
        this.billingCodesLOCData = [...this.hospiceLOCBillingCodesSetup];
      }
      this.utilityService.decreaseSpinnerCount();
    }
  }

  mapPeriodResponseMedicareCheckbox() {
    let newData = [];
    for (var key in this.periodResponse.codes) {
      if (!this.periodResponse.codes.hasOwnProperty(key)) continue;
      var obj = this.periodResponse.codes[key];
      let oldValue = this.billingCodesLOCData.find(
        (value) => value.Description == obj.description
      );
      let newPeriod: BillingBenefitCode = {
        RevenueCode: obj.revenueCode,
        HcpcsCode: obj.hcpcsCode,
        Description: obj.description,
        Service: key,
        M1: oldValue ? oldValue.M1 : obj.m1,
        M2: oldValue ? oldValue.M2 : obj.m2,
        M3: oldValue ? oldValue.M3 : obj.m3,
        M4: oldValue ? oldValue.M4 : obj.m4,
      };

      newData.push(newPeriod);
    }

    this.billingCodesLOCData = [];
    this.billingCodesLOCData = newData;

    const order = [
      "Routine Home Care (1-60)",
      "Routine Home Care (61+)",
      "Continuous Home Care",
      "General Inpatient Care",
      "Respite Care",
    ];
    this.billingCodesLOCData = this.billingCodesLOCData.sort((a, b) => {
      const indexA = order.indexOf(a.Description);
      const indexB = order.indexOf(b.Description);
      return indexA - indexB;
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/billing-codes/hospice-loc/hospice-loc.ts